/**
 * Client-side "grab the first frame as a JPEG poster" helper.
 *
 * Why client-side? The alternative is running ffmpeg on the backend, which
 * adds a heavy native dep + a whole processing pipeline. Doing it in the
 * user's browser gives us a good-enough thumbnail with zero backend surface,
 * and the resulting JPEG rides along on the same upload the user is
 * already waiting for. Works in every evergreen browser.
 *
 * Seeks a hair past t=0 (0.5s) so we don't capture the black leader frame
 * that most encoders emit at t=0.
 */
export async function captureFirstFrame(file, { maxWidth = 1280, quality = 0.72 } = {}) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    video.src = url;

    let done = false;
    const cleanup = () => {
      if (done) return;
      done = true;
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
      video.load();
    };

    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('Thumbnail capture timed out (video may be corrupt or codec not supported).'));
    }, 15000);

    video.onloadedmetadata = () => {
      // Some codecs need a seek to force a keyframe render — 0.5s is late
      // enough to skip the black leader and early enough to be inside every
      // clip we'd ever accept.
      const target = Math.min(0.5, (video.duration || 1) / 2);
      video.currentTime = target;
    };

    video.onseeked = () => {
      try {
        const scale = Math.min(1, maxWidth / (video.videoWidth || maxWidth));
        const w = Math.round((video.videoWidth || maxWidth) * scale);
        const h = Math.round((video.videoHeight || 720) * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            clearTimeout(timeout);
            cleanup();
            if (!blob) return reject(new Error('Could not encode thumbnail JPEG.'));
            resolve(blob);
          },
          'image/jpeg',
          quality,
        );
      } catch (err) {
        clearTimeout(timeout);
        cleanup();
        reject(err);
      }
    };

    video.onerror = () => {
      clearTimeout(timeout);
      cleanup();
      reject(new Error('Could not load the video for thumbnail capture (unsupported codec?).'));
    };
  });
}
